import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { startVersionWatcher, persistCurrentVersion, APP_VERSION, getStoredVersion, fetchRemoteVersion, type RemoteVersionInfo } from "@/lib/appVersion";
import { resetClientCache } from "@/lib/cacheReset";
import { CoralPin } from "@/components/branding/CoralPin";
import { tAuto } from "@/lib/tAuto";

/**
 * Compares two dotted version strings. Returns -1, 0, or 1.
 * Falls back to lexicographic compare when segments aren't numeric (our format is date-based).
 */
function compareVersions(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Shows an elegant toast when a newer build is detected.
 * Auto-reloads after a short countdown; user can dismiss to delay.
 * If the remote version.json defines `minSupportedVersion` and the current
 * build is below it, shows a blocking modal directing the user to the store.
 */
export function UpdateBanner() {
  const [show, setShow] = useState(false);
  const [seconds, setSeconds] = useState(3);
  const [forcedUpdate, setForcedUpdate] = useState<RemoteVersionInfo | null>(null);

  // Detect stale install + check minimum supported version at boot
  useEffect(() => {
    const prev = getStoredVersion();
    if (prev && prev !== APP_VERSION) {
      resetClientCache({ clearStorage: false }).catch(() => {});
    }
    persistCurrentVersion();

    // Startup minimum-version gate
    fetchRemoteVersion().then((info) => {
      if (info?.minSupportedVersion && compareVersions(APP_VERSION, info.minSupportedVersion) < 0) {
        setForcedUpdate(info);
      }
    });
  }, []);

  // Poll for new versions
  useEffect(() => {
    const stop = startVersionWatcher((info) => {
      if (info.minSupportedVersion && compareVersions(APP_VERSION, info.minSupportedVersion) < 0) {
        setForcedUpdate(info);
      } else {
        setShow(true);
      }
    });
    return stop;
  }, []);

  // Countdown to auto-reload
  useEffect(() => {
    if (!show) return;
    if (seconds <= 0) {
      void doReload();
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [show, seconds]);

  const doReload = async () => {
    try {
      await resetClientCache({ clearStorage: false });
    } finally {
      window.location.reload();
    }
  };

  const openStore = () => {
    if (!forcedUpdate?.storeUrls) return;
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();
    const url = platform === "ios" ? forcedUpdate.storeUrls.ios : forcedUpdate.storeUrls.android;
    if (url) window.open(url, isNative ? "_system" : "_blank");
  };

  // ---- Forced update modal (blocking) ----
  if (forcedUpdate) {
    return (
      <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-sm w-full bg-white rounded-2xl p-6 shadow-2xl text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <CoralPin className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-lg font-bold text-foreground mb-2">{tAuto("update.mandatoryTitle", "Atualização obrigatória")}</h2>
          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            Uma nova versão da BeraStays está disponível. Para continuar a usar a plataforma, atualiza a aplicação.
          </p>
          {Capacitor.isNativePlatform() ? (
            <button
              onClick={openStore}
              className="w-full h-11 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-colors"
            >
              Abrir loja e atualizar
            </button>
          ) : (
            <button
              onClick={() => void doReload()}
              className="w-full h-11 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-colors"
            >
              Atualizar agora
            </button>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 240 }}
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw]"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3 bg-white/95 backdrop-blur-xl border border-primary/15 shadow-[0_12px_36px_-10px_rgba(232,81,61,0.35)] rounded-2xl px-4 py-3 pr-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <CoralPin className="w-4.5 h-4.5 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-foreground leading-tight">
                Nova versão disponível
              </p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                A atualizar em {seconds}s…
              </p>
            </div>
            <button
              onClick={() => void doReload()}
              className="ml-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 transition-colors px-3 py-2 rounded-xl"
            >
              Atualizar agora
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default UpdateBanner;

